import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Chip,
    Card,
    CardContent,
    TextField,
    useMediaQuery,
    Collapse,
    IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import EventInfo from "../components/EventInfo";
import StatusCard from "../components/StatusCard";
import {
    getCodexFromLocation,
    useLiveData,
    getRunning,
    heatHasLiveRunner,
    pickAutoHeat,
    excelSerialToDateString,
    formatPenalty,
    sexLabel,
    isTelemarkDiscipline,
    getInterCount,
    matchesCategory,
    startSecondsToClientMs,
} from "../timeserver/uselivedata.js";

// 🚦 Un concurrent de la startlist est "au départ, pas encore parti" —
// même logique que startlistStatusLabel() plus bas (pas de temps final,
// pas de statut DNS/DNF/DSQ, pas encore en piste).
function isAtStart(s) {
    if (s.final_time != null) return false;
    if (s.status && s.status !== "OK") return false;
    if (s.start_status === "STARTED") return false;
    return true;
}

// ---------------------------------------------------------------------
// Temps TRONQUÉS au 1/100 (règle FIS, jamais arrondis) — 29.10.2026.
// Les secondes reçues ont une précision au millième : on passe d'abord en
// ms entières (Math.round) pour éliminer les erreurs de virgule flottante
// (ex. 117.98999…), puis on tronque au centième.
// ---------------------------------------------------------------------
const toCs = (sec) => Math.floor(Math.round(Number(sec) * 1000) / 10);
const truncSec = (sec) => (sec == null || sec === "" ? null : toCs(sec) / 100);

function formatDuration(sec) {
    if (sec == null || sec === "" || Number.isNaN(Number(sec))) return "";
    const neg = Number(sec) < 0;
    const cs = toCs(Math.abs(Number(sec)));
    const m = Math.floor(cs / 6000);
    const s = Math.floor((cs % 6000) / 100);
    const c = cs % 100;
    return `${neg ? "-" : ""}${m}:${String(s).padStart(2, "0")}.${String(c).padStart(2, "0")}`;
}

// Temps qui tourne : au dixième, tronqué.
function formatRunning(sec) {
    if (sec == null || sec < 0) return "";
    const ds = Math.floor(Math.round(sec * 1000) / 100);
    const m = Math.floor(ds / 600);
    const s = Math.floor((ds % 600) / 10);
    return `${m}:${String(s).padStart(2, "0")}.${ds % 10}`;
}

// Secondes depuis minuit à l'heure de la SUISSE (Europe/Zurich), heure
// d'été / d'hiver comprise — 29.10.2026 : l'ancien calcul gardait le
// décalage d'été (+1 h de trop en hiver). Les heures de départ de
// SkiAlpPro sont en heure locale suisse.
const zurichParts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Zurich",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
});
function zurichSecondsSinceMidnight(ms) {
    const parts = zurichParts.formatToParts(new Date(ms));
    const get = (t) => Number(parts.find((x) => x.type === t)?.value || 0);
    return get("hour") * 3600 + get("minute") * 60 + get("second") + (ms % 1000) / 1000;
}

// Temps d'un dossard dans une manche (tronqué, null si pas de temps valide).
function heatTimeOf(heat, bib) {
    const c = (heat?.competitors || []).find((x) => String(x.bib) === String(bib));
    return c && c.status === "OK" && c.final_time != null ? truncSec(c.final_time) : null;
}

// Rangs d'une manche (dossard -> rang), sur les temps tronqués.
function heatRanks(heat) {
    const list = (heat?.competitors || [])
        .filter((c) => c.status === "OK" && c.final_time != null)
        .map((c) => ({ bib: String(c.bib), cs: toCs(c.final_time) }))
        .sort((a, b) => a.cs - b.cs);
    const out = {};
    let rank = 0;
    list.forEach((x, i) => {
        if (i === 0 || x.cs !== list[i - 1].cs) rank = i + 1;
        out[x.bib] = rank;
    });
    return out;
}

export default function LiveTiming() {
    const codex = getCodexFromLocation();
    const { data, connected } = useLiveData(codex);

    // Références stables : data.heats / data.parameters peuvent être des
    // objets "neufs" à chaque rendu (fallback `|| {}`), ce qui casserait la
    // mémoïsation de heatKeys plus bas si on ne les stabilise pas ici.
    const heats = useMemo(() => data.heats || {}, [data.heats]);
    const parameters = useMemo(() => data.parameters || {}, [data.parameters]);
    const finalRanking = useMemo(
        () => data.final_ranking || { type: "sum", ranking: [] },
        [data.final_ranking]
    );

    // Config d'affichage (titre custom, logos, bandeau) — indépendante des
    // données SkiAlpPro, éditable depuis la page de configuration (Home.jsx).
    const [config, setConfig] = useState({});
    useEffect(() => {
        if (!codex) return;
        fetch(`/api/config/${encodeURIComponent(codex)}`)
            .then((r) => (r.ok ? r.json() : {}))
            .then((cfg) => setConfig(cfg || {}))
            .catch(() => {});
    }, [codex]);

    const [selectedHeat, setSelectedHeat] = useState(null); // null = auto (suit le direct)
    const [viewMode, setViewMode] = useState("live"); // "live" | "startlist" | "general"
    const [filterYears, setFilterYears] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null); // null = Toutes
    const [courseInfoOpen, setCourseInfoOpen] = useState(false);

    const categories = config.categories || [];
    const selectedCategory = categories.find((c) => c.id === selectedCategoryId) || null;

    const isMobile = useMediaQuery("(max-width: 900px)");

    // Toutes les manches présentes dans les données (1 à 5 et plus : aucune
    // limite côté affichage).
    const heatKeys = useMemo(
        () => Object.keys(heats).sort((a, b) => Number(a) - Number(b)),
        [heats]
    );

    const effectiveHeat =
        selectedHeat !== null && heats[selectedHeat] ? selectedHeat : pickAutoHeat(heats);

    const heatData = effectiveHeat ? heats[effectiveHeat] : null;
    const competitors = heatData?.competitors || [];
    const rawStartlist = heatData?.startlist || [];

    const running = getRunning(competitors);

    // Temps qui tourne calculé ici (heure suisse), rafraîchi tous les 1/10 s.
    const [nowMs, setNowMs] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNowMs(Date.now()), 100);
        return () => clearInterval(t);
    }, []);
    const nowZurich = zurichSecondsSinceMidnight(nowMs);
    const elapsedByBib = {};
    running.forEach((c) => {
        const start = Number(c.start_seconds_since_midnight);
        if (Number.isNaN(start)) return;
        let e = nowZurich - start;
        if (e < -3600) e += 86400; // passage de minuit
        elapsedByBib[c.bib] = Math.max(0, e);
    });

    // Filtre combiné : catégorie dynamique (Config.jsx) + années de naissance
    // en texte libre. S'applique aux résultats ET à la liste de départ.
    const applyFilters = (list) => {
        let out = list;
        if (selectedCategory) {
            out = out.filter((c) => matchesCategory(c.year, c.sex, selectedCategory));
        }
        if (filterYears.length > 0) {
            out = out.filter((c) => filterYears.includes(String(c.year ?? "").trim()));
        }
        return out;
    };

    // Recalcule rang + écart pour un sous-ensemble filtré (déjà trié par
    // temps croissant, hérité du tri fait par listener.py) : le premier de
    // la liste devient rang 1 / écart 0.000.
    const recomputeRanking = (list) => {
        if (list.length === 0) return list;
        const best = list[0].final_time;
        return list.map((c, i) => ({
            ...c,
            rank: i + 1,
            gap: c.final_time != null && best != null ? (toCs(c.final_time) - toCs(best)) / 100 : null,
        }));
    };

    const ranked = recomputeRanking(
        applyFilters(competitors.filter((c) => c.status === "OK" && c.final_time))
    );
    const unranked = applyFilters(competitors.filter((c) => c.status !== "OK"));
    const startlist = applyFilters(rawStartlist);
    const filteredRunning = applyFilters(running);

    // ---------------------------------------------------------------------
    // Temps cumulé jusqu'à la manche affichée — 08.10.2026.
    // Addition des manches 1..N (finalRanking.type "sum") ou meilleure
    // manche parmi 1..N ("best"), avec rang provisoire sur ce cumul. Affiché
    // à partir de la 2e manche.
    // ---------------------------------------------------------------------
    const heatIndex = heatKeys.indexOf(String(effectiveHeat));
    const keysSoFar = heatIndex >= 0 ? heatKeys.slice(0, heatIndex + 1) : [];
    const showTotal = keysSoFar.length > 1;
    const totalMode = finalRanking.type === "best" ? "best" : "sum";
    // Libellé du résultat de l'épreuve, comme le bouton du classement général.
    const totalLabel = totalMode === "best" ? "Meilleur" : "Total";

    const totalOf = (bib) => {
        const times = keysSoFar.map((k) => ({ heat: k, t: heatTimeOf(heats[k], bib) }));
        if (totalMode === "sum") {
            if (times.some((x) => x.t == null)) return null;
            return { t: times.reduce((s, x) => s + x.t, 0), heat: null };
        }
        const valid = times.filter((x) => x.t != null);
        if (!valid.length) return null;
        return valid.reduce((a, b) => (b.t < a.t ? b : a));
    };

    // Rang sur le cumul parmi les classés de la manche affichée (filtres
    // compris), écart au meilleur cumul.
    const totalsByBib = {};
    if (showTotal) {
        const list = ranked
            .map((c) => ({ bib: c.bib, total: totalOf(c.bib) }))
            .filter((x) => x.total != null)
            .sort((a, b) => a.total.t - b.total.t);
        const bestTotal = list[0]?.total.t;
        // (totaux = sommes de temps déjà tronqués ; formatDuration repasse par les ms)
        let rank = 0;
        list.forEach((x, i) => {
            if (i === 0 || x.total.t !== list[i - 1].total.t) rank = i + 1;
            totalsByBib[String(x.bib)] = { ...x.total, rank, gap: x.total.t - bestTotal };
        });
    }

    // 🚦 Prochain au départ : premier de la startlist encore "au départ"
    // (pas parti), dans l'ordre de départ prévu (line).
    const nextStarter =
        startlist
            .filter(isAtStart)
            .sort((a, b) => (a.line ?? Infinity) - (b.line ?? Infinity))[0] || null;

    // 🏁 3 dernières arrivées (heure d'arrivée = départ + temps final).
    const lastArrivals = ranked
        .map((c) => {
            const startMs = startSecondsToClientMs(c.start_seconds_since_midnight);
            const arrivalMs = startMs != null ? startMs + c.final_time * 1000 : null;
            return { ...c, arrivalMs };
        })
        .filter((c) => c.arrivalMs != null)
        .sort((a, b) => b.arrivalMs - a.arrivalMs)
        .slice(0, 3);

    const telemark = isTelemarkDiscipline(parameters);
    const interCount = telemark ? getInterCount(parameters) : 0;

    const rankedRows = ranked.map((c) => {
        const tot = totalsByBib[String(c.bib)];
        const row = {
            id: c.bib,
            rank: c.rank,
            bib: c.bib,
            lastName: c.last_name,
            firstName: c.first_name,
            sex: sexLabel(c.sex),
            nation: c.nation,
            club: c.club,
            yearOfBirth: c.year,
            timeSec: formatDuration(c.final_time),
            diff: c.gap != null ? "+" + formatDuration(c.gap) : "",
            status: c.status,
            totalTime: tot ? formatDuration(tot.t) + (tot.heat ? ` (M${tot.heat})` : "") : showTotal ? "—" : "",
            totalRank: tot ? tot.rank : "",
            totalDiff: tot && tot.gap > 0 ? "+" + formatDuration(tot.gap) : "",
        };
        if (telemark) {
            for (let i = 0; i < interCount; i++) {
                row[`inter${i + 1}`] = formatDuration(c.inters?.[i]);
            }
            row.penGate = formatPenalty(c.pen_gate);
            row.penJump = formatPenalty(c.pen_jump);
        }
        return row;
    });

    const unrankedRows = unranked.map((c) => {
        const row = {
            id: c.bib,
            bib: c.bib,
            lastName: c.last_name,
            firstName: c.first_name,
            sex: sexLabel(c.sex),
            nation: c.nation,
            club: c.club,
            yearOfBirth: c.year,
            status: c.status,
        };
        if (telemark) {
            for (let i = 0; i < interCount; i++) {
                row[`inter${i + 1}`] = formatDuration(c.inters?.[i]);
            }
            row.penGate = formatPenalty(c.pen_gate);
            row.penJump = formatPenalty(c.pen_jump);
        }
        return row;
    });

    const startlistRows = startlist.map((s) => ({
        id: s.bib,
        line: s.line,
        bib: s.bib,
        lastName: s.last_name,
        firstName: s.first_name,
        sex: sexLabel(s.sex),
        nation: s.nation,
        club: s.club,
        yearOfBirth: s.year,
        status: startlistStatusLabel(s),
    }));

    const startlistColumns = [
        { field: "line", headerName: "Ordre", flex: 0.4, minWidth: 60 },
        { field: "bib", headerName: "Dossard", flex: 0.6, minWidth: 70 },
        { field: "lastName", headerName: "Nom", flex: 1, minWidth: 100 },
        { field: "firstName", headerName: "Prénom", flex: 1, minWidth: 100 },
        { field: "sex", headerName: "Sexe", flex: 0.5, minWidth: 70 },
        { field: "nation", headerName: "Nation", flex: 0.7, minWidth: 70 },
        { field: "club", headerName: "Club", flex: 1, minWidth: 120 },
        { field: "yearOfBirth", headerName: "Année", flex: 0.6, minWidth: 80 },
        { field: "status", headerName: "Statut", flex: 0.9, minWidth: 130 },
    ];

    const interColumns = telemark
        ? Array.from({ length: interCount }, (_, i) => ({
            field: `inter${i + 1}`,
            headerName: `Inter ${i + 1}`,
            flex: 0.7,
            minWidth: 90,
        }))
        : [];

    const penaltyColumns = telemark
        ? [
            { field: "penGate", headerName: "Pén. gates", flex: 0.6, minWidth: 90 },
            { field: "penJump", headerName: "Pén. jump", flex: 0.6, minWidth: 90 },
        ]
        : [];

    // Colonnes du cumul (à partir de la 2e manche).
    const totalColumns = showTotal
        ? [
            {
                field: "totalTime",
                headerName:
                    totalMode === "sum"
                        ? `Total M${keysSoFar[0]}–M${keysSoFar[keysSoFar.length - 1]}`
                        : "Meilleure manche",
                flex: 1,
                minWidth: 130,
            },
            ...(totalMode === "sum" ? [{ field: "totalDiff", headerName: "Diff. total", flex: 0.8, minWidth: 90 }] : []),
            { field: "totalRank", headerName: "Rg total", flex: 0.5, minWidth: 70 },
        ]
        : [];

    const columns = [
        { field: "rank", headerName: "Rang", flex: 0.5, minWidth: 60 },
        { field: "bib", headerName: "Dossard", flex: 0.7, minWidth: 70 },
        { field: "lastName", headerName: "Nom", flex: 1, minWidth: 100 },
        { field: "firstName", headerName: "Prénom", flex: 1, minWidth: 100 },
        { field: "sex", headerName: "Sexe", flex: 0.5, minWidth: 70 },
        { field: "nation", headerName: "Nation", flex: 0.7, minWidth: 70 },
        { field: "club", headerName: "Club", flex: 1, minWidth: 120 },
        { field: "yearOfBirth", headerName: "Année", flex: 0.7, minWidth: 70 },
        ...interColumns,
        ...penaltyColumns,
        { field: "timeSec", headerName: `Temps M${effectiveHeat ?? ""}`, flex: 0.9, minWidth: 110 },
        { field: "diff", headerName: "Diff", flex: 0.8, minWidth: 90 },
        ...totalColumns,
    ];

    // Classement général (toutes manches combinées, addition ou meilleure
    // manche selon TPARAMETERS.FINAL_RANKING_TYPE — calculé par listener.py).
    const generalFilteredRaw = applyFilters(finalRanking.ranking || []);
    const generalCountedRaw = recomputeRanking(
        generalFilteredRaw.filter((r) => r.status === "OK" && r.final_time != null)
    );
    const generalNotCountedRaw = generalFilteredRaw.filter(
        (r) => !(r.status === "OK" && r.final_time != null)
    );

    // Temps de chaque manche dans le classement général (M1…M5).
    const generalRows = generalCountedRaw.map((r) => {
        const row = {
            id: r.bib,
            rank: r.rank,
            bib: r.bib,
            lastName: r.last_name,
            firstName: r.first_name,
            sex: sexLabel(r.sex),
            nation: r.nation,
            club: r.club,
            yearOfBirth: r.year,
            timeSec: formatDuration(r.final_time),
            diff: r.gap != null ? "+" + formatDuration(r.gap) : "",
            status: r.status,
        };
        heatKeys.forEach((k) => {
            const t = heatTimeOf(heats[k], r.bib);
            row[`heat${k}`] = t != null ? formatDuration(t) : "—";
        });
        return row;
    });

    const generalNotCountedRows = generalNotCountedRaw.map((r) => ({
        id: r.bib,
        bib: r.bib,
        lastName: r.last_name,
        firstName: r.first_name,
        sex: sexLabel(r.sex),
        nation: r.nation,
        club: r.club,
        yearOfBirth: r.year,
        status: r.status,
    }));

    const generalColumns = [
        { field: "rank", headerName: "Rang", flex: 0.5, minWidth: 60 },
        { field: "bib", headerName: "Dossard", flex: 0.7, minWidth: 70 },
        { field: "lastName", headerName: "Nom", flex: 1, minWidth: 100 },
        { field: "firstName", headerName: "Prénom", flex: 1, minWidth: 100 },
        { field: "sex", headerName: "Sexe", flex: 0.5, minWidth: 70 },
        { field: "nation", headerName: "Nation", flex: 0.7, minWidth: 70 },
        { field: "club", headerName: "Club", flex: 1, minWidth: 120 },
        { field: "yearOfBirth", headerName: "Année", flex: 0.7, minWidth: 70 },
        ...heatKeys.map((k) => ({ field: `heat${k}`, headerName: `M${k}`, flex: 0.7, minWidth: 85 })),
        {
            field: "timeSec",
            headerName: finalRanking.type === "best" ? "Meilleur temps" : "Total",
            flex: 0.9,
            minWidth: 110,
        },
        { field: "diff", headerName: "Diff", flex: 0.8, minWidth: 90 },
    ];

    const gridStyles = {
        "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#022a5b",
            borderBottom: "3px solid #f1242e",
        },
        "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#022a5b",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
            color: "#ffffff",
            fontWeight: 600,
        },
        "& .MuiDataGrid-columnSeparator": {
            color: "#ffffff",
        },
        "& .MuiDataGrid-cell": { borderBottom: "1px solid #c2c9cd", color: "#022a5b" },
        "& .MuiDataGrid-row:hover": { backgroundColor: "#f1242e", color: "#ffffff" },
    };

    // Infos évènement (titre/date/lieu) : la config custom prime, sinon on
    // retombe sur ce qui vient directement de TPARAMETERS.
    const eventInfo = {
        name:
            config.eventTitle ||
            [parameters.TITLE1, parameters.TITLE2].filter(Boolean).join(" — ") ||
            "Événement",
        date: config.eventDate || excelSerialToDateString(parameters.DATE),
        location: parameters.LOCATION || "",
        logoLeft: config.logoLeft || "",
        logoRight: config.logoRight || "",
        showTicker: config.showTicker || false,
        tickerMessage: config.tickerMessage || "",
    };

    // Infos piste / traceur pour la manche affichée
    const courseInfo = effectiveHeat
        ? {
            traceur: [
                parameters[`HEADER_FIS_COURSESETTER_1NAME_${effectiveHeat}`],
                parameters[`HEADER_FIS_COURSESETTER_${effectiveHeat}`],
            ]
                .filter(Boolean)
                .join(" "),
            course: parameters[`HEADER_FIS_COURSE_${effectiveHeat}`],
            gates: parameters[`HEADER_FIS_GATE_${effectiveHeat}`],
            length: parameters[`HEADER_FIS_LENGTH_${effectiveHeat}`],
            drop: parameters[`HEADER_FIS_DROP_${effectiveHeat}`],
            startTime: parameters[`HEADER_FIS_STARTTIME_${effectiveHeat}`],
        }
        : null;

    const hasCourseInfo =
        courseInfo && (courseInfo.traceur || courseInfo.course || courseInfo.gates);

    const courseInfoBody = courseInfo && (
        <>
            {courseInfo.traceur && (
                <Typography variant="body2">Traceur : {courseInfo.traceur}</Typography>
            )}
            {courseInfo.course && (
                <Typography variant="body2">Piste : {courseInfo.course}</Typography>
            )}
            {courseInfo.gates && (
                <Typography variant="body2">Portes : {courseInfo.gates}</Typography>
            )}
            {courseInfo.length && (
                <Typography variant="body2">Longueur : {courseInfo.length} m</Typography>
            )}
            {courseInfo.drop && (
                <Typography variant="body2">Dénivelé : {courseInfo.drop} m</Typography>
            )}
            {courseInfo.startTime && (
                <Typography variant="body2">Départ prévu : {courseInfo.startTime}</Typography>
            )}
        </>
    );

    return (
        <Box sx={{ width: "100%", p: 2, position: "relative" }}>
            <EventInfo event={eventInfo} />

            {hasCourseInfo && isMobile && (
                // Vue mobile : carte repliable inline, fermée par défaut.
                <Card sx={{ mb: 2 }}>
                    <CardContent
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: "10px !important",
                            cursor: "pointer",
                        }}
                        onClick={() => setCourseInfoOpen((v) => !v)}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                            Manche {effectiveHeat} — Piste
                        </Typography>
                        <IconButton
                            size="small"
                            sx={{
                                transform: courseInfoOpen ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.3s ease",
                            }}
                        >
                            <ExpandMoreIcon />
                        </IconButton>
                    </CardContent>
                    <Collapse in={courseInfoOpen} timeout="auto" unmountOnExit>
                        <CardContent sx={{ pt: 0 }}>{courseInfoBody}</CardContent>
                    </Collapse>
                </Card>
            )}

            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2 }}>
                {/* Colonne gauche : tous les filtres/chips, empilés sans espace perdu */}
                <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip
                            label="🏁 Live / résultats"
                            color={viewMode === "live" ? "primary" : "default"}
                            onClick={() => setViewMode("live")}
                            sx={{ fontWeight: "bold" }}
                        />
                        <Chip
                            label="📋 Liste de départ"
                            color={viewMode === "startlist" ? "primary" : "default"}
                            onClick={() => setViewMode("startlist")}
                            sx={{ fontWeight: "bold" }}
                        />
                        {(generalRows.length > 0 || generalNotCountedRows.length > 0) && (
                            <Chip
                                label={
                                    "🏆 Classement général" +
                                    (finalRanking.type === "best" ? " (meilleure manche)" : " (addition)")
                                }
                                color={viewMode === "general" ? "primary" : "default"}
                                onClick={() => setViewMode("general")}
                                sx={{ fontWeight: "bold" }}
                            />
                        )}
                    </Box>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                        <Chip
                            label={connected ? "LIVE" : "OFFLINE"}
                            color={connected ? "success" : "error"}
                            sx={{ fontWeight: "bold" }}
                        />
                        <Chip
                            label="🔴 Auto (suivre le direct)"
                            color={selectedHeat === null ? "primary" : "default"}
                            onClick={() => setSelectedHeat(null)}
                            sx={{ fontWeight: "bold" }}
                        />
                    </Box>

                    {/* Onglets de manches (autant que de manches reçues) */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {heatKeys.map((key) => (
                            <Chip
                                key={key}
                                label={`Manche ${key}` + (heatHasLiveRunner(heats[key]) ? " ● LIVE" : "")}
                                color={key === effectiveHeat ? "primary" : "default"}
                                variant={heatHasLiveRunner(heats[key]) ? "filled" : "outlined"}
                                onClick={() => setSelectedHeat(key)}
                                sx={{ fontWeight: "bold" }}
                            />
                        ))}
                    </Box>

                    {/* Filtre par catégorie (défini dans la config de l'évènement) */}
                    {categories.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <Chip
                                label="Toutes catégories"
                                color={selectedCategoryId === null ? "primary" : "default"}
                                onClick={() => setSelectedCategoryId(null)}
                            />
                            {categories.map((cat) => (
                                <Chip
                                    key={cat.id}
                                    label={cat.name || "(sans nom)"}
                                    color={selectedCategoryId === cat.id ? "primary" : "default"}
                                    onClick={() => setSelectedCategoryId(cat.id)}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Colonne droite : carte traceur/piste (desktop uniquement) */}
                {hasCourseInfo && !isMobile && (
                    <Card sx={{ maxWidth: 260, flexShrink: 0, boxShadow: 4 }}>
                        <CardContent sx={{ p: "10px !important" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                                Manche {effectiveHeat} — Piste
                            </Typography>
                            {courseInfoBody}
                        </CardContent>
                    </Card>
                )}
            </Box>

            {!heatData && viewMode !== "general" && (
                <Typography sx={{ mt: 3 }}>Aucune donnée pour cette manche.</Typography>
            )}

            {heatData && viewMode === "startlist" && (
                <>
                    <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                        Manche {effectiveHeat} — Liste de départ
                    </Typography>
                    {startlist.length === 0 ? (
                        <Typography>Liste de départ non disponible pour cette manche.</Typography>
                    ) : isMobile ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {startlistRows.map((row) => (
                                <StatusCard key={row.bib} competitor={row} compact />
                            ))}
                        </Box>
                    ) : (
                        <DataGrid
                            autoHeight
                            rows={startlistRows}
                            columns={startlistColumns}
                            pageSize={100}
                            rowsPerPageOptions={[100]}
                            disableSelectionOnClick
                            rowHeight={36}
                            sx={{ width: "100%", ...gridStyles }}
                        />
                    )}
                </>
            )}

            {heatData && viewMode === "live" && (
                <>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 2 }}>
                        <TextField
                            label="Années (ex: 2005,2006)"
                            variant="outlined"
                            size="small"
                            onBlur={(e) =>
                                setFilterYears(
                                    e.target.value
                                        .split(",")
                                        .map((y) => y.trim())
                                        .filter((y) => y !== "")
                                )
                            }
                        />
                    </Box>

                    {/* Au départ */}
                    {nextStarter && (
                        <Card sx={{ mb: 2, borderLeft: "4px solid #1976d2" }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    Au départ :
                                </Typography>
                                <Box
                                    sx={
                                        isMobile
                                            ? { display: "flex", flexDirection: "column", gap: 0.25 }
                                            : {
                                                display: "grid",
                                                gridTemplateColumns: "50px 250px auto",
                                                alignItems: "center",
                                                gap: 1,
                                            }
                                    }
                                >
                                    {isMobile ? (
                                        <>
                                            <Typography sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                                                #{nextStarter.bib} {nextStarter.first_name} {nextStarter.last_name}
                                            </Typography>
                                            <Typography color="text.secondary">
                                                Ordre {nextStarter.line ?? "—"}
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Typography sx={{ fontWeight: 700, textAlign: "right" }}>
                                                #{nextStarter.bib}
                                            </Typography>
                                            <Typography>
                                                {nextStarter.first_name} {nextStarter.last_name}
                                            </Typography>
                                            <Typography sx={{ textAlign: "right" }} color="text.secondary">
                                                Ordre {nextStarter.line ?? "—"}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* En piste */}
                    {filteredRunning.length > 0 && (
                        <Card sx={{ mb: 2, borderLeft: "4px solid orange" }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    En piste :
                                </Typography>
                                {filteredRunning.map((c) => {
                                    // Temps de la manche précédente (et son rang), dès la manche 2.
                                    const prevKey = heatIndex > 0 ? heatKeys[heatIndex - 1] : null;
                                    const prevTime = prevKey ? heatTimeOf(heats[prevKey], c.bib) : null;
                                    const prevRank = prevKey ? heatRanks(heats[prevKey])[String(c.bib)] : null;
                                    return (
                                        <Box
                                            key={c.bib}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: isMobile ? "44px 1fr auto" : "50px 250px 110px auto",
                                                alignItems: "center",
                                                columnGap: 1,
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 700, textAlign: "right" }}>#{c.bib}</Typography>
                                            <Typography sx={{ wordBreak: "break-word" }}>
                                                {c.first_name} {c.last_name}
                                            </Typography>
                                            <Typography sx={{ textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                                                {formatRunning(elapsedByBib[c.bib] ?? 0)}
                                            </Typography>
                                            {prevKey && (
                                                <Typography
                                                    color="text.secondary"
                                                    sx={{ gridColumn: isMobile ? "2 / 4" : "auto", fontVariantNumeric: "tabular-nums" }}
                                                >
                                                    Manche {prevKey} : {prevTime != null ? formatDuration(prevTime) : "—"}
                                                    {prevRank ? ` (${prevRank}e)` : ""}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                            </CardContent>
                        </Card>
                    )}

                    {/* 3 dernières arrivées */}
                    {lastArrivals.length > 0 && (
                        <Card sx={{ mb: 2, borderLeft: "4px solid #2e7d32" }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    3 dernières arrivées :
                                </Typography>
                                {isMobile ? (
                                    lastArrivals.map((c) => {
                                        const tot = totalsByBib[String(c.bib)];
                                        return (
                                            <Box
                                                key={c.bib}
                                                sx={{ py: 0.75, borderBottom: "1px solid #eee", "&:last-of-type": { borderBottom: "none" } }}
                                            >
                                                <Typography sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                                                    #{c.bib} {c.first_name} {c.last_name}
                                                </Typography>
                                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto auto", columnGap: 1.5 }}>
                                                    <Typography variant="body2" color="text.secondary">Manche {effectiveHeat}</Typography>
                                                    <Typography sx={{ fontWeight: 600, textAlign: "right" }}>{formatDuration(c.final_time)}</Typography>
                                                    <Typography color="text.secondary" sx={{ textAlign: "right", minWidth: 40 }}>{c.rank ? `${c.rank}e` : "—"}</Typography>
                                                    {showTotal && (
                                                        <>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {totalLabel}{tot?.heat ? ` (M${tot.heat})` : ""}
                                                            </Typography>
                                                            <Typography sx={{ fontWeight: 700, textAlign: "right" }}>{tot ? formatDuration(tot.t) : "—"}</Typography>
                                                            <Typography sx={{ fontWeight: 700, textAlign: "right", minWidth: 40 }}>{tot ? `${tot.rank}e` : ""}</Typography>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })
                                ) : (
                                    // Desktop : une seule grille pour toutes les lignes, afin
                                    // que chaque colonne commence au même endroit.
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: showTotal
                                                ? "minmax(220px, 300px) auto auto auto auto auto auto auto"
                                                : "minmax(220px, 300px) auto auto auto",
                                            columnGap: 2,
                                            rowGap: 0.5,
                                            alignItems: "baseline",
                                            justifyContent: "start",
                                        }}
                                    >
                                        {lastArrivals.map((c) => {
                                            const tot = totalsByBib[String(c.bib)];
                                            return (
                                                <React.Fragment key={c.bib}>
                                                    <Typography sx={{ fontWeight: 700 }}>
                                                        #{c.bib} {c.first_name} {c.last_name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">Manche {effectiveHeat}</Typography>
                                                    <Typography sx={{ fontWeight: 600, textAlign: "right" }}>{formatDuration(c.final_time)}</Typography>
                                                    <Typography color="text.secondary" sx={{ textAlign: "right" }}>{c.rank ? `${c.rank}e` : "—"}</Typography>
                                                    {showTotal && (
                                                        <>
                                                            <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>{totalLabel}</Typography>
                                                            <Typography sx={{ fontWeight: 700, textAlign: "right" }}>{tot ? formatDuration(tot.t) : "—"}</Typography>
                                                            <Typography sx={{ fontWeight: 700, textAlign: "right" }}>{tot ? `${tot.rank}e` : ""}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{tot?.heat ? `(M${tot.heat})` : ""}</Typography>
                                                        </>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Classement */}
                    <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                        Manche {effectiveHeat} — Classés
                        {showTotal ? (totalMode === "sum" ? " (+ total des manches)" : " (+ meilleure manche)") : ""}
                    </Typography>

                    {isMobile ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {rankedRows.map((row) =>
                                showTotal ? (
                                    <MobileResultCard
                                        key={row.bib}
                                        row={row}
                                        total={totalsByBib[String(row.bib)]}
                                        totalLabel={totalLabel}
                                    />
                                ) : (
                                    <StatusCard key={row.bib} competitor={row} compact />
                                )
                            )}
                        </Box>
                    ) : (
                        <DataGrid
                            autoHeight
                            rows={rankedRows}
                            columns={columns}
                            pageSize={20}
                            rowsPerPageOptions={[20]}
                            disableSelectionOnClick
                            rowHeight={36}
                            sx={{ width: "100%", ...gridStyles }}
                        />
                    )}

                    {/* Non classés */}
                    {unrankedRows.length > 0 && (
                        <>
                            <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>
                                Non classés (DNS / DNF / DSQ / DSQP)
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {unrankedRows.map((e) => (
                                    <StatusCard key={e.bib} competitor={e} />
                                ))}
                            </Box>
                        </>
                    )}
                </>
            )}

            {viewMode === "general" && (
                <>
                    <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                        Classement général —{" "}
                        {finalRanking.type === "best"
                            ? "meilleure manche retenue"
                            : "addition des manches"}
                    </Typography>

                    {generalRows.length === 0 ? (
                        <Typography>Classement général non disponible pour l'instant.</Typography>
                    ) : isMobile ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {generalRows.map((row) => (
                                <StatusCard key={row.bib} competitor={row} compact />
                            ))}
                        </Box>
                    ) : (
                        <DataGrid
                            autoHeight
                            rows={generalRows}
                            columns={generalColumns}
                            pageSize={100}
                            rowsPerPageOptions={[100]}
                            disableSelectionOnClick
                            rowHeight={36}
                            sx={{ width: "100%", ...gridStyles }}
                        />
                    )}

                    {/* Résultats non pris en compte (critères non remplis dans une des manches) */}
                    {generalNotCountedRows.length > 0 && (
                        <>
                            <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>
                                Résultats non pris en compte
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {generalNotCountedRows.map((r) => (
                                    <StatusCard key={r.bib} competitor={r} />
                                ))}
                            </Box>
                        </>
                    )}
                </>
            )}
        </Box>
    );
}

function startlistStatusLabel(s) {
    if (s.final_time != null) return `Classé${s.rank ? " (" + s.rank + "e)" : ""}`;
    if (s.status && s.status !== "OK") return s.status;
    if (s.start_status === "STARTED") return "En piste";
    return "AT START";
}

// Carte mobile d'un classé quand il y a un résultat d'épreuve (manche 2+) :
// temps de manche ET total / meilleur dans la même ligne d'en-tête, détails
// (nation, club, sexe, année) dépliables — 08.10.2026.
function MobileResultCard({ row, total, totalLabel }) {
    const [open, setOpen] = useState(false);
    return (
        <Card sx={{ borderLeft: "4px solid #f1242e" }}>
            <CardContent sx={{ p: "10px 12px !important" }}>
                <Box
                    sx={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", columnGap: 1.5, alignItems: "center", cursor: "pointer" }}
                    onClick={() => setOpen((v) => !v)}
                >
                    <Typography sx={{ fontWeight: 700, minWidth: 0, wordBreak: "break-word" }}>
                        <Box component="span" sx={{ mr: 1 }}>{row.rank}</Box>
                        <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", mr: 0.5 }}>#{row.bib}</Box>
                        {row.firstName} {row.lastName}
                    </Typography>
                    {/* Deux colonnes construites à l'identique (même taille et même
                        hauteur de ligne) : les temps et les lignes du dessous
                        sont alignés horizontalement d'une colonne à l'autre. */}
                    <TimeStack top={row.timeSec} bottom={row.diff || "+0:00.00"} italic />
                    <TimeStack
                        top={total ? formatDuration(total.t) : "—"}
                        bottom={`${totalLabel}${total ? ` · ${total.rank}e` : ""}`}
                        divider
                    />
                    <IconButton
                        size="small"
                        sx={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}
                    >
                        <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">Nation: {row.nation || "—"}</Typography>
                        <Typography variant="body2">Club: {row.club || "—"}</Typography>
                        <Typography variant="body2">Sexe: {row.sex || "—"}</Typography>
                        <Typography variant="body2">Année: {row.yearOfBirth || "—"}</Typography>
                        {total?.heat && <Typography variant="body2">Meilleur temps en manche {total.heat}</Typography>}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
}

// Bloc « temps / ligne du dessous » à hauteur fixe, utilisé pour le temps de
// manche et pour le total / meilleur, afin qu'ils soient parfaitement alignés.
function TimeStack({ top, bottom, italic = false, divider = false }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateRows: "22px 16px",
                alignItems: "center",
                justifyItems: "end",
                pl: divider ? 1.25 : 0,
                borderLeft: divider ? "1px solid #c2c9cd" : "none",
                minWidth: 78,
            }}
        >
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: "22px", fontVariantNumeric: "tabular-nums" }}>
                {top}
            </Typography>
            <Typography
                sx={{ fontSize: "0.75rem", lineHeight: "16px", color: "text.secondary", fontStyle: italic ? "italic" : "normal", whiteSpace: "nowrap" }}
            >
                {bottom}
            </Typography>
        </Box>
    );
}